import React, { useEffect, useState } from "react"
import { useAuthState } from "react-firebase-hooks/auth"
import { Link, useNavigate } from "react-router-dom"
import { auth, signInWithGoogle } from "./firebase"
import "./static/Register.css"
import Background from "./static/research-login-background.png"
import { registerWithEmailAndPassword } from "./firebase"
import api from "./api_ui"

import googleLogo from "./static/google_logo2.png"
import Button from "@mui/material/Button"
import CssBaseline from "@mui/material/CssBaseline"
import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"
import { createTheme, ThemeProvider, styled } from "@mui/material/styles"

import Toast from "./common/Toast"

const GoogleLogo = styled("img")({
    width: "100%",
    height: "100%",
    borderRadius: "50%"
})

const Copyright = (props) => {
    return (
        <Typography
            variant='body2'
            color='text.secondary'
            align='center'
            {...props}>
            {"Copyright © "}
            <Link color='inherit' href={process.env.REACT_APP_WEBSITE_URL}>
                FinSoftAi
            </Link>{" "}
            {new Date().getFullYear()}
            {"."}
        </Typography>
    )
}

const theme = createTheme()

function Register() {
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [name, setName] = useState("")
    const [user, loading, error] = useAuthState(auth)
    const [isRegistering, setIsRegistering] = useState(false)
    const [toast, setToast] = useState({
        open: false,
        message: "",
        severity: "info"
    })
    const navigate = useNavigate()

    const showToast = (message, severity = "info") => {
        setToast({ open: true, message, severity })
    }

    const closeToast = () => {
        setToast({ ...toast, open: false })
    }

    const register = async () => {
        // Basic validation
        if (!name.trim()) {
            showToast("Please enter your full name", "error")
            return
        }
        if (!email.trim()) {
            showToast("Please enter your email address", "error")
            return
        }
        if (!password.trim()) {
            showToast("Please enter a password", "error")
            return
        }
        if (password.length < 6) {
            showToast("Password must be at least 6 characters long", "error")
            return
        }

        setIsRegistering(true)

        try {
            const result = await registerWithEmailAndPassword(
                name,
                email,
                password
            )

            if (result?.success) {
                // Save user data to backend
                try {
                    const userSaveResult = await api.add_user({
                        id: result.user.uid,
                        name: name,
                        email: email,
                        isVerified: result.user.emailVerified
                    })
                    console.log("User saved to backend successfully")
                } catch (saveError) {
                    console.error("Error saving user to backend:", saveError)
                    // Don't block registration if backend save fails
                }

                showToast(result.message, "success")
                // Wait for toast to show for 5 seconds, then redirect
                setTimeout(() => {
                    navigate("/login", {
                        replace: true,
                        state: {
                            message:
                                "Registration successful! Please check your email to verify your account before logging in.",
                            email: email
                        }
                    })
                }, 5000)
            } else {
                showToast(
                    result?.error || "Registration failed. Please try again.",
                    "error"
                )
            }
        } catch (err) {
            console.error("Registration error:", err)
            showToast("Registration failed. Please try again.", "error")
        } finally {
            setIsRegistering(false)
        }
    }

    useEffect(() => {
        if (loading) return
        if (user && !isRegistering) {
            // Save user data to backend for Google sign-ins
            const saveUserData = async () => {
                try {
                    const userSaveResult = await api.add_user({
                        id: user.uid,
                        name: user.displayName || name,
                        email: user.email,
                        isVerified: user.emailVerified
                    })
                    console.log("User saved to backend successfully")
                } catch (saveError) {
                    console.error("Error saving user to backend:", saveError)
                    // Don't block flow if backend save fails
                }
            }

            // Save user data first, then sign out and redirect
            saveUserData().then(() => {
                auth.signOut().then(() => {
                    // Check if user is verified (Google accounts are automatically verified)
                    const isVerified = user.emailVerified
                    const message = isVerified
                        ? "Registration successful! You can now login with your account."
                        : "Registration successful! Please check your email to verify your account before logging in."

                    navigate("/login", {
                        replace: true,
                        state: {
                            message: message,
                            email: email
                        }
                    })
                })
            })
        }
    }, [user, loading, isRegistering])

    return (
        <ThemeProvider theme={theme}>
            <Grid container component='main' sx={{ height: "100vh" }}>
                <CssBaseline />
                <Grid
                    justifyContent={"flex-end"}
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    sx={{
                        backgroundImage: `url(${Background})`,
                        // backgroundRepeat: 'no-repeat',
                        backgroundColor: (t) =>
                            t.palette.mode === "light"
                                ? t.palette.grey[50]
                                : t.palette.grey[900],
                        backgroundSize: "cover",
                        backgroundPosition: "center"
                        // backgroundColor: 'blue'
                    }}>
                    <Box
                        className='register_box'
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "end",
                            height: "100%",
                            width: "100%",
                            backgroundColor: "transparent",
                            pr: "5%"
                        }}>
                        <Box sx={{ mt: "auto", mb: "auto", width: "25%" }}>
                            <div className='register__container container'>
                                <div className='row gx-3 justify-content-center'>
                                    <Typography
                                        variant='body1'
                                        style={{
                                            color: "#2563eb",
                                            fontWeight: 600,
                                            marginBottom: 16,
                                            textAlign: "center",
                                            lineHeight: 1.5,
                                            width: "100%",
                                            margin: "10px auto"
                                        }}>
                                        This product is optimized for desktop
                                        and laptop use only. For the best
                                        experience, please access it from a
                                        computer.
                                    </Typography>
                                    <Typography
                                        className='mb-2 col-12'
                                        component='h3'
                                        variant='h4'
                                        style={{
                                            color: "#858585",
                                            fontWeight: 400,
                                            textAlign: "center"
                                        }}>
                                        Create Account
                                    </Typography>
                                    <input
                                        type='text'
                                        className='register__textBox col-md-12 col-sm-12'
                                        value={name}
                                        onChange={(e) =>
                                            setName(e.target.value)
                                        }
                                        placeholder='Full Name'
                                    />
                                    <input
                                        type='text'
                                        className='register__textBox col-md-5 col-sm-12'
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        placeholder='E-mail Address'
                                    />
                                    <div className='col-md-auto'></div>
                                    <input
                                        type='password'
                                        className='register__textBox col-md-5 col-sm-12'
                                        value={password}
                                        onChange={(e) =>
                                            setPassword(e.target.value)
                                        }
                                        placeholder='Password'
                                    />
                                    <button
                                        className='button bg-green-400'
                                        onClick={register}
                                        disabled={isRegistering}
                                        style={{
                                            opacity: isRegistering ? 0.7 : 1,
                                            cursor: isRegistering
                                                ? "not-allowed"
                                                : "pointer"
                                        }}>
                                        {isRegistering
                                            ? "Creating Account..."
                                            : "Register"}
                                    </button>

                                    <div>
                                        Already have an account?
                                        <br /> <Link to='/login'>
                                            Login
                                        </Link>{" "}
                                        now.
                                    </div>
                                    <div>
                                        <hr className='inline-hr' />
                                        <span
                                            style={{
                                                fontSize: 15,
                                                fontWeight: 300,
                                                marginTop: 10,
                                                color: "#858585"
                                            }}>
                                            or login with
                                        </span>
                                        <hr className='inline-hr' />
                                    </div>
                                    <div
                                        style={{
                                            width: "100%",
                                            textAlign: "center"
                                        }}>
                                        <Button
                                            onClick={signInWithGoogle}
                                            variant='contained'
                                            sx={{
                                                borderRadius: "50%",
                                                minWidth: 0,
                                                p: 1,
                                                m: 1,
                                                boxShadow:
                                                    "0px 1px 5px rgba(0, 0, 0, 0.2)",
                                                backgroundColor: "white",
                                                width: 50,
                                                height: 50
                                            }}>
                                            <GoogleLogo
                                                src={googleLogo}
                                                alt='Google'
                                            />
                                        </Button>
                                    </div>

                                    <Copyright sx={{ mt: 2 }} />
                                </div>
                            </div>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <Toast
                open={toast.open}
                message={toast.message}
                severity={toast.severity}
                onClose={closeToast}
            />
        </ThemeProvider>
    )
}
export default Register
